<template>
  <div class="xp-bar">
    <p class="xp-bar-head">
      <span class="xp-bar-chip" :class="{ bumped: leveledUp }">
        {{ t('level.chip', { n: level }) }}
      </span>
      <span v-if="progress.xpDelta > 0" class="xp-bar-delta">
        {{ t('level.xpGain', { n: progress.xpDelta }) }}
      </span>
    </p>
    <div
      class="xp-track"
      :class="{ flash: leveledUp }"
      role="progressbar"
      :aria-valuenow="Math.round(value)"
      :aria-valuemin="0"
      :aria-valuemax="max"
      :aria-label="t('level.xpProgress')"
    >
      <span class="xp-fill" :style="{ width: fillPct + '%' }" />
    </div>
    <p class="xp-bar-text">
      {{ t('level.bar', { into: Math.round(value), need: max }) }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import {
  snapshotFromXp,
  type DuelXpResult,
} from '@shared/index'
import { useI18n } from '../i18n/t'
import {
  buildXpSegments,
  tweenSegments,
  type BarTween,
} from './progressTween'

const props = defineProps<{
  progress: DuelXpResult
  play: boolean
}>()

const emit = defineEmits<{ done: [] }>()

const { t } = useI18n()

const before = computed(() =>
  snapshotFromXp(Math.max(0, props.progress.xp - props.progress.xpDelta)),
)
const segments = computed(() => buildXpSegments(before.value, props.progress))

const value = ref(0)
const max = ref(1)
const level = ref(1)
const leveledUp = ref(false)
let tween: BarTween | null = null

const fillPct = computed(() =>
  max.value > 0 ? Math.max(0, Math.min(100, (value.value / max.value) * 100)) : 100,
)

function stop(): void {
  tween?.cancel()
  tween = null
}

function skip(): void {
  tween?.skip()
}

function start(): void {
  stop()
  leveledUp.value = false

  const list = segments.value
  const first = list[0]
  value.value = first?.from ?? props.progress.xpIntoLevel
  max.value = first?.max || 1
  level.value = first?.step ?? props.progress.level

  tween = tweenSegments(
    list,
    (next, segment, index) => {
      value.value = next
      max.value = segment.max || 1
      level.value = segment.step
      if (index > 0) leveledUp.value = true
    },
    () => {
      tween = null
      if (list.length > 1) leveledUp.value = true
      emit('done')
    },
  )
}

watch(
  () => [props.play, props.progress] as const,
  ([play]) => {
    if (play) start()
    else stop()
  },
  { immediate: true },
)

onBeforeUnmount(stop)

defineExpose({ skip })
</script>

<style scoped>
.xp-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
}

.xp-bar-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.xp-bar-chip {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 999px;
  border: 1px solid #4b5563;
  background: #1f2937;
  color: #e5e7eb;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.xp-bar-chip.bumped {
  border-color: #facc15;
  color: #facc15;
  animation: chipBump 560ms var(--md-motion-emphasized, cubic-bezier(0.05, 0.7, 0.1, 1));
}

.xp-bar-delta {
  color: #7dd3fc;
  font-size: 14px;
  font-weight: 800;
  animation: xpRise 520ms var(--md-motion-emphasized, cubic-bezier(0.05, 0.7, 0.1, 1));
}

.xp-track {
  position: relative;
  width: 280px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
}

.xp-track.flash {
  animation: trackFlash 620ms var(--md-motion-standard, cubic-bezier(0.2, 0, 0, 1));
}

.xp-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #0ea5e9, #7dd3fc);
  box-shadow: 0 0 10px rgba(125, 211, 252, 0.5);
}

.xp-bar-text {
  margin: 0;
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

@keyframes chipBump {
  0% { transform: scale(0.8); }
  55% { transform: scale(1.22); }
  100% { transform: scale(1); }
}

@keyframes xpRise {
  from { transform: translateY(6px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes trackFlash {
  0% { box-shadow: 0 0 0 rgba(125, 211, 252, 0); }
  40% { box-shadow: 0 0 20px rgba(125, 211, 252, 0.8); }
  100% { box-shadow: 0 0 0 rgba(125, 211, 252, 0); }
}

@media (prefers-reduced-motion: reduce) {
  .xp-bar-chip.bumped,
  .xp-bar-delta,
  .xp-track.flash {
    animation: none;
  }
}
</style>
